import { useEffect, useRef } from "react";
import {
  AttributionControl,
  Map as MLMap,
  NavigationControl,
  ScaleControl,
  addProtocol,
  config as maplibreConfig,
} from "maplibre-gl";

// MapLibre v6 is ESM-only and loads its worker as a sibling module. Any module
// Vite serves gets an `/@vite/client` import injected, which touches `document`
// and therefore throws inside a worker — leaving style loading stalled forever
// with no error event. `scripts/sync-maplibre-worker.mjs` copies the worker into
// `public/`, which Vite serves untransformed, and we point MapLibre at it.
const MAPLIBRE_WORKER_URL = "/maplibre/maplibre-gl-worker.mjs";
import { Protocol } from "pmtiles";
import "maplibre-gl/dist/maplibre-gl.css";

import { LAYER_REGISTRY } from "./registry";
import { appStyle, firstSymbolLayerId } from "./style";
import { useLayerStore } from "../state/useLayerStore";
import { useSelectionStore } from "../state/useSelectionStore";
import { useMapStore } from "./useMapStore";
import { IDF_BOUNDS } from "./bounds";
import {
  PRICE_FILL_LAYERS,
  PRICE_LABEL_LAYERS,
  priceColorExpression,
  priceLabelExpression,
} from "./layers/prices";
import { LEVELS, levelDef } from "./levels";
import { REPAINTERS, applyRepainters } from "./repaint";
import {
  TRANSIT_DIMMABLE,
  TRANSIT_LABEL_LAYER,
  TRANSIT_VIEW_LAYERS,
  lineLabelFilter,
  lineOpacity,
} from "./layers/transit";

// Register the pmtiles:// protocol once for the lifetime of the page, so
// MapLibre can range-request tiles straight out of the static archives.
maplibreConfig.WORKER_URL = MAPLIBRE_WORKER_URL;

const protocol = new Protocol();
addProtocol("pmtiles", protocol.tile);

/** Île-de-France, framed on Paris. */
const INITIAL_VIEW = { center: [2.3488, 48.8534] as [number, number], zoom: 10.2 };

export function MapView({ children }: { children?: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MLMap | null>(null);
  const readyRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const style = appStyle();
    const map = new MLMap({
      container: containerRef.current,
      style,
      ...INITIAL_VIEW,
      maxBounds: IDF_BOUNDS,
      minZoom: 8,
      maxZoom: 17,
      attributionControl: false,
      // Keep tiles for panned-away areas in memory; re-panning is then instant.
      maxTileCacheSize: 512,
      // Lets the WebGL surface be captured in screenshots, which is how the
      // map gets visually verified during development. Off in production
      // because it costs a little memory and blocks some driver fast paths.
      canvasContextAttributes: { preserveDrawingBuffer: import.meta.env.DEV },
    });
    mapRef.current = map;
    if (import.meta.env.DEV) {
      // Handy for poking at style/source state from the console.
      (window as unknown as { map: MLMap }).map = map;
    }

    // MapLibre reports tile, style and source failures through this event
    // rather than throwing, so without it a broken layer just renders nothing.
    map.on("error", (e) => {
      console.error("[maplibre]", e.error?.message ?? e);
    });

    map.addControl(new NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new ScaleControl({ maxWidth: 120, unit: "metric" }), "bottom-left");
    map.addControl(new AttributionControl({ compact: true }), "bottom-right");

    // `style.load`, not `load`: `load` additionally waits for the first frame
    // to paint, which never arrives if the map starts in a background tab or a
    // display:none container — and then no data layer is ever added. Sources
    // and layers only need the style parsed, which is what this event means.
    map.on("zoom", () => useLayerStore.getState().setZoom(map.getZoom()));
    map.on("movestart", () => coolWarmLayers(map));

    map.on("style.load", () => {
      const anchor = firstSymbolLayerId(style);

      // Add every registered layer up front, hidden or shown according to the
      // store. Nothing is added or removed after this point — toggling is a
      // visibility flip, which costs a repaint and no network traffic.
      //
      // Each definition is isolated: a layer whose tiles have not been built
      // yet should disable itself, not take down every layer registered after
      // it. That is exactly the situation mid-pipeline, while one archive is
      // still being generated.
      const unavailable: string[] = [];
      for (const def of LAYER_REGISTRY) {
        try {
          for (const [id, spec] of Object.entries(def.sources)) {
            if (!map.getSource(id)) map.addSource(id, spec);
          }
          const visible = useLayerStore.getState().isVisible(def.id);
          for (const layer of def.layers) {
            map.addLayer(
              {
                ...layer,
                layout: {
                  ...layer.layout,
                  visibility:
                    visible || ALWAYS_VISIBLE.has(layer.id) ? "visible" : "none",
                },
              } as never,
              def.beforeId ?? anchor,
            );
          }
        } catch (err) {
          console.error(`[layer:${def.id}] unavailable —`, err);
          unavailable.push(def.id);
        }
      }
      if (unavailable.length) useLayerStore.getState().setUnavailable(unavailable);

      // The selection outline goes above every data layer, whatever order the
      // registry put them in. It is the only feedback a click gives, and a
      // second choropleth drawn over it — population over prices — buries it.
      for (const l of LEVELS) {
        if (map.getLayer(l.selectedId)) map.moveLayer(l.selectedId, anchor);
      }

      // The layers were just added with only their on/off state applied; this
      // additionally hides the levels that are not currently in force.
      applyVisibility(map, useLayerStore.getState());
      applyHighlight(map, useLayerStore.getState().highlightedLines);
      // The registry defines each shadeable layer at its default measure; a
      // returning user has one persisted, and it has to be applied before the
      // first paint rather than on the next change.
      applyRepainters(map);
      readyRef.current = true;
      attachSelection(map);
      map.resize();
      useMapStore.getState().setMap(map);
    });

    return () => {
      readyRef.current = false;
      useMapStore.getState().setMap(null);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Recolour for the selected year.
  //
  // This is a paint-property change on geometry the browser already has: every
  // year ships as its own attribute in the same tile, so scrubbing the slider
  // costs a repaint and never a network request.
  useEffect(() => {
    let lastYear = useLayerStore.getState().year;
    return useLayerStore.subscribe((state) => {
      const map = mapRef.current;
      if (!map || !readyRef.current || state.year === lastYear) return;
      lastYear = state.year;
      const color = priceColorExpression(state.year);
      for (const id of PRICE_FILL_LAYERS) {
        if (map.getLayer(id)) {
          map.setPaintProperty(id, "fill-color", color as never);
        }
      }
      const label = priceLabelExpression(state.year);
      for (const id of PRICE_LABEL_LAYERS) {
        if (map.getLayer(id)) {
          map.setLayoutProperty(id, "text-field", label as never);
        }
      }
      if (map.getLayer("sales-circle")) {
        map.setFilter("sales-circle", ["==", ["get", "year"], state.year] as never);
      }
    });
  }, []);

  // Re-shade every layer whose measure is chosen in a pane — population,
  // hazard declarations, the climate grid, the heat island.
  //
  // Same contract as the year slider: every measure rides along as its own
  // attribute on the same feature, so switching one is a paint change on
  // geometry already in memory. See `map/repaint.ts` for the list.
  useEffect(() => {
    const stops = REPAINTERS.map((repainter) => {
      let last = repainter.key();
      return repainter.subscribe(() => {
        const map = mapRef.current;
        if (!map || !readyRef.current) return;
        const next = repainter.key();
        if (next === last) return;
        last = next;
        repainter.apply(map);
      });
    });
    return () => stops.forEach((stop) => stop());
  }, []);

  // Dim the transit lines the user has not picked out of the legend.
  //
  // Like the year slider, this is a paint change on geometry the browser
  // already holds — the tiles do not move, so focusing a line is instant.
  useEffect(() => {
    let last = useLayerStore.getState().highlightedLines;
    return useLayerStore.subscribe((state) => {
      const map = mapRef.current;
      if (!map || !readyRef.current || state.highlightedLines === last) return;
      last = state.highlightedLines;
      applyHighlight(map, last);
    });
  }, []);

  // Highlight whichever zone is currently selected.
  useEffect(() => {
    return useSelectionStore.subscribe((state) => {
      const map = mapRef.current;
      if (!map || !readyRef.current) return;
      for (const l of LEVELS) {
        if (!map.getLayer(l.selectedId)) continue;
        const code =
          state.selection && state.selection.scope === l.id
            ? state.selection.code
            : " ";
        map.setFilter(l.selectedId, ["==", ["get", "code"], code] as never);
      }
    });
  }, []);

  // Apply visibility changes.
  //
  // Subscribing outside React's render loop keeps a toggle from re-rendering
  // the tree that owns the map canvas.
  useEffect(() => {
    return useLayerStore.subscribe((state) => {
      const map = mapRef.current;
      if (!map || !readyRef.current) return;
      applyVisibility(map, state);
    });
  }, []);

  return (
    <>
      {/*
        Sized with h-full/w-full rather than `absolute inset-0`: MapLibre's own
        stylesheet sets `.maplibregl-map { position: relative }`, which has the
        same specificity as Tailwind's `.absolute` but is imported later and so
        wins. The element then stops being positioned, `inset-0` no longer sizes
        it, and it collapses to zero height — a blank white page with a map
        rendering into a 300px default canvas nobody can see.
      */}
      <div ref={containerRef} className="h-full w-full" />
      {children}
    </>
  );
}

/**
 * Clicking the map selects the zone under the cursor.
 *
 * Which geography answers depends on zoom: communes read better when the whole
 * region is in view, IRIS neighbourhoods once you are close enough for the
 * difference between two ends of a commune to matter.
 */
function attachSelection(map: MLMap) {
  map.on("click", (e) => {
    const level = useLayerStore.getState().activeLevel();
    const def = levelDef(level);
    const layer = def.hitId;
    if (!map.getLayer(layer)) return;
    const [feature] = map.queryRenderedFeatures(e.point, { layers: [layer] });
    if (!feature) {
      useSelectionStore.getState().select(null);
      return;
    }
    const props = feature.properties ?? {};

    // The green grid is already loaded wherever the user can see it, so read
    // the cell under the cursor rather than asking the API for it.
    const [greenCell] = map.getLayer("green-fill")
      ? map.queryRenderedFeatures(e.point, { layers: ["green-fill"] })
      : [];
    const g = greenCell?.properties;

    const [envCell] = map.getLayer("noise-fill")
      ? map.queryRenderedFeatures(e.point, {
          // Query the source layer through whichever of the two is present;
          // both draw the same polygons, so either answers.
          layers: ["noise-fill", "air-fill"].filter((l) => map.getLayer(l)),
        })
      : [];
    const en = envCell?.properties;

    useSelectionStore.getState().select({
      scope: level,
      code: String(props.code ?? ""),
      name: String(props.nom ?? props.code ?? "Unknown"),
      parents: parentsOf(level, props),
      properties: props,
      point: { lon: e.lngLat.lng, lat: e.lngLat.lat },
      green: g
        ? {
            score: Number(g.score),
            greenPct: Number(g.green_pct),
            parkM: Number(g.park_m),
          }
        : undefined,
      airNoise:
        en && typeof en.air === "number" && typeof en.noise === "number"
          ? { air: en.air, noise: en.noise }
          : undefined,
    });
  });

  for (const id of LEVELS.map((l) => l.hitId)) {
    map.on("mouseenter", id, () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", id, () => {
      map.getCanvas().style.cursor = "";
    });
  }
}

/**
 * The chain of larger areas containing a zone, for the breadcrumb.
 *
 * The names come from the tile attributes rather than a lookup: a neighbourhood
 * feature already carries the code and name of its commune, and both carry
 * their department.
 */
function parentsOf(level: string, props: Record<string, unknown>) {
  const chain: { level: "departement" | "commune"; code: string; name: string }[] = [];
  const dep = props.dep ? String(props.dep) : null;
  if (dep && level !== "departement") {
    chain.push({ level: "departement", code: dep, name: DEPARTEMENT_NAMES[dep] ?? dep });
  }
  if (level === "iris" && props.commune) {
    chain.push({
      level: "commune",
      code: String(props.commune),
      name: String(props.nom_commune ?? props.commune),
    });
  }
  return chain;
}

const DEPARTEMENT_NAMES: Record<string, string> = {
  "75": "Paris",
  "77": "Seine-et-Marne",
  "78": "Yvelines",
  "91": "Essonne",
  "92": "Hauts-de-Seine",
  "93": "Seine-Saint-Denis",
  "94": "Val-de-Marne",
  "95": "Val-d'Oise",
};

/**
 * Bring the lines the user picked forward and push the rest back.
 *
 * An empty list means no focus at all, which restores each layer's own base
 * opacity rather than dimming everything.
 */
function applyHighlight(map: MLMap, highlighted: string[]) {
  for (const { id, base } of TRANSIT_DIMMABLE) {
    if (map.getLayer(id)) {
      map.setPaintProperty(id, "line-opacity", lineOpacity(highlighted, base) as never);
    }
  }
  if (map.getLayer(TRANSIT_LABEL_LAYER)) {
    map.setFilter(TRANSIT_LABEL_LAYER, lineLabelFilter(highlighted) as never);
  }
}

/**
 * Show exactly the layers that should be visible right now.
 *
 * Three things decide it: whether the user has the layer switched on, and —
 * for the two layers that carry several renderings of the same thing — which
 * one is currently chosen. Prices carry three geographies and show one; transit
 * carries stops and lines and shows one.
 */
const ALWAYS_VISIBLE = new Set(LEVELS.flatMap((l) => [l.hitId, l.selectedId]));

/**
 * Layers switched off but held warm: painted fully transparent rather than
 * hidden, so their source stays used and keeps its parsed tiles. Keyed by
 * layer id, holding the paint property and the real value to put back.
 *
 * See `keepWarm` in `types.ts` for why this exists at all.
 */
type OpacityProp = "fill-opacity" | "line-opacity" | "circle-opacity";

const warmLayers = new Map<string, { prop: OpacityProp; value: unknown }>();

/**
 * Let the warm layers go, once the camera has moved.
 *
 * Toggling a layer off and on to compare two readings of the same place
 * happens in place. Panning somewhere else does not, and a warm layer goes on
 * fetching and parsing tiles for wherever the map ends up — for the air-noise
 * archive that is megabytes of work for something nobody can see. So the
 * warmth buys exactly one instant re-toggle at the view it was earned in.
 *
 * Called on `movestart` rather than `moveend`: by the end of a pan the warm
 * layer has already pulled tiles for the ground it crossed, which is the cost
 * this exists to avoid.
 */
function coolWarmLayers(map: MLMap) {
  if (warmLayers.size === 0) return;
  for (const [id, { prop, value }] of warmLayers) {
    if (!map.getLayer(id)) continue;
    map.setPaintProperty(id, prop, value as never);
    map.setLayoutProperty(id, "visibility", "none");
  }
  warmLayers.clear();
}

function applyVisibility(map: MLMap, state: ReturnType<typeof useLayerStore.getState>) {
  const active = state.activeLevel();
  const hidden = new Set([
    ...LEVELS.filter((l) => l.id !== active).flatMap((l) => [
      l.hitId, l.fillId, l.lineId, l.labelId, l.selectedId,
    ]),
    ...Object.entries(TRANSIT_VIEW_LAYERS)
      .filter(([view]) => view !== state.transitView)
      .flatMap(([, ids]) => ids),
  ]);

  for (const def of LAYER_REGISTRY) {
    const visible = state.visibility[def.id] ?? def.defaultVisible;
    for (const layer of def.layers) {
      if (!map.getLayer(layer.id)) continue;
      const current = map.getLayoutProperty(layer.id, "visibility") ?? "visible";
      // Clicking a zone and seeing which one is selected belong to the map
      // itself, so those two layers ignore the price toggle. Everything else
      // follows it.
      const on = ALWAYS_VISIBLE.has(layer.id) || visible;
      const wanted = on && !hidden.has(layer.id);

      // An expensive layer being switched off is painted away instead, so its
      // tiles survive and switching it back on is a repaint. Only from a state
      // where it is actually on screen: a layer that was already off has no
      // tiles to keep, and warming it would start loading them for nothing.
      const warmable =
        def.keepWarm && !hidden.has(layer.id) &&
        (current === "visible" || warmLayers.has(layer.id));
      if (warmable) {
        const prop = `${layer.type}-opacity` as OpacityProp;
        const own =
          (layer.paint as Record<string, unknown> | undefined)?.[prop] ?? 1;
        if (wanted) {
          if (warmLayers.delete(layer.id)) map.setPaintProperty(layer.id, prop, own as never);
        } else if (!warmLayers.has(layer.id)) {
          warmLayers.set(layer.id, { prop, value: own });
          map.setPaintProperty(layer.id, prop, 0 as never);
        }
        if (current !== "visible") map.setLayoutProperty(layer.id, "visibility", "visible");
        continue;
      }

      const next = wanted ? "visible" : "none";
      if (current !== next) map.setLayoutProperty(layer.id, "visibility", next);
    }
  }
}
